import React, { FC, useEffect, useState } from 'react';
import translate from '@translate';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { setLocationParams } from 'src/resources/location/reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import api from '@api';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { GeisPackingType, TransportPackageType } from 'src/types/transportPackageType';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import TopBlock from './TopBlock';
import Detail from './Detail';

type GeisDetail = GeisPackingType & { PACKAGE_TYPE_SSRCS?: string[] };

const GeisPackingTypeDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { code } = useParams();
    const backUrl = '/transports/geis-packing-types/';

    const [data, setData] = useState<GeisDetail | null>(null);
    const [selectedSsrcs, setSelectedSsrcs] = useState<string[]>([]);
    const [packageTypeOptions, setPackageTypeOptions] = useState<ConstantsCodeType[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const permissionUpdate = useCheckPermission('TRANSPORT_PACKAGE_TYPE.UPDATE');
    const permissionDetail = useCheckPermission('TRANSPORT_PACKAGE_TYPE.DETAIL');
    const permissionList = useCheckPermission('TRANSPORT_PACKAGE_TYPE.LIST');
    const canView = permissionDetail || permissionList || permissionUpdate;
    const disableEdit = !permissionUpdate;

    useEffect(() => {
        if (!code) {
            navigate(backUrl);
            return;
        }
        dispatch(
            setLocationParams({
                name: translate('geisPackingTypeDetail.title', { code }),
                backUrl,
            })
        );
        void loadDetail(code);
        void loadPackageTypes();
    }, [code]);

    const loadDetail = async (geisCode: string) => {
        setLoading(true);
        try {
            const res = await api(
                'get',
                `api/secure/transports/geis-packing-types/${encodeURIComponent(geisCode)}`
            );
            const row: GeisDetail = res.data?.data ?? res.data;
            setData(row);
            setSelectedSsrcs(row.PACKAGE_TYPE_SSRCS ?? (row.PACKAGE_TYPE_SSRC ? [row.PACKAGE_TYPE_SSRC] : []));
        } catch {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('geisPackingTypeDetail.message.loadError'),
                })
            );
            navigate(backUrl);
        } finally {
            setLoading(false);
        }
    };

    const loadPackageTypes = async () => {
        try {
            const res = await api('get', 'api/secure/transports/package-types?active=0');
            const rows: TransportPackageType[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data)
                  ? res.data
                  : [];
            setPackageTypeOptions(
                rows.map((row) => ({
                    label: `${row.SSRC_CODE} — ${row.NAME}`,
                    value: row.SSRC_CODE,
                }))
            );
        } catch {
            setPackageTypeOptions([]);
        }
    };

    const handleSave = async () => {
        if (!code || disableEdit) {
            return;
        }
        setSaving(true);
        try {
            await api('put', 'api/secure/transports/geis-packing-types/mappings', {
                items: [
                    {
                        CODE: code,
                        PACKAGE_TYPE_SSRCS: selectedSsrcs,
                    },
                ],
            });
            dispatch(
                addMessage({
                    type: 'success',
                    title: translate('geisPackingTypes.message.mappingsSaved'),
                })
            );
            await loadDetail(code);
        } catch {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('geisPackingTypes.message.mappingsError'),
                })
            );
        } finally {
            setSaving(false);
        }
    };

    if (!canView) {
        return null;
    }

    return (
        <div data-testid="geis-packing-type-detail">
            <TopBlock disableEdit={disableEdit || saving} handleSave={() => void handleSave()} />
            {(loading || !data) && <Loading />}
            {data && (
                <Detail
                    data={data}
                    selectedSsrcs={selectedSsrcs}
                    packageTypeOptions={packageTypeOptions}
                    disableEdit={disableEdit || saving}
                    onChangeSsrcs={setSelectedSsrcs}
                />
            )}
        </div>
    );
};

export default GeisPackingTypeDetailContainer;
